import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../auth/AuthContext";
import ProfileLayout from "../components/ProfileLayout";
import { SITE_EMAILS } from "../lib/siteConfig";
import { pageItems } from "../lib/pageItems";
import { Mail, MessageSquare, Bell, ExternalLink } from "lucide-react";

function ChannelPill({ channel }) {
  const isEmail = channel === "email";
  const Icon = isEmail ? Mail : MessageSquare;
  return (
    <span
      className={
        "inline-flex items-center gap-1 px-2 h-6 rounded-full zc-badge-pill " +
        (isEmail
          ? "bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]"
          : "bg-[rgba(37,99,235,0.10)] text-[var(--zc-secondary,#2563eb)]")
      }
    >
      <Icon size={11} /> {channel}
    </span>
  );
}

export default function ProfileMessages() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/messages/me`, { withCredentials: true });
        setItems(pageItems(r.data));
      } finally { setLoading(false); }
    })();
  }, []);

  const visible = items.filter(i => filter === "all" ? true : i.channel === filter);

  return (
    <ProfileLayout eyebrow="Profile" title="Email & SMS">
      <p className="text-sm text-[var(--zc-text-2)] mb-4 max-w-2xl">
        Delivery log for email and SMS notifications (confirmations, account updates). These are one-way — you cannot reply here.
        To message your host or renter, open the trip from{" "}
        <Link to="/profile/bookings" className="text-[var(--zc-accent)] font-semibold hover:underline">
          My bookings
        </Link>{" "}
        and use Trip messages.
      </p>

      <div className="flex items-center gap-2 mb-4" data-testid="messages-filters">
        {["all", "email", "sms"].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={
              "px-3 h-8 rounded-full text-xs font-semibold uppercase tracking-widest transition " +
              (filter === f
                ? "bg-[var(--zc-accent)] text-white"
                : "bg-[var(--zc-bg-3)] text-[var(--zc-text-2)] hover:text-[var(--zc-text)]")
            }
            data-testid={`messages-filter-${f}`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="zc-card overflow-hidden" data-testid="messages-list">
        {loading && <div className="px-6 py-12 text-center text-[var(--zc-text-3)] text-sm">Loading…</div>}
        {!loading && visible.length === 0 && (
          <div className="px-6 py-16 text-center text-[var(--zc-text-3)]">
            <Bell size={28} className="mx-auto mb-2 opacity-60" />
            <div className="text-sm font-semibold text-[var(--zc-text-2)]">No notifications yet</div>
            <div className="text-sm mt-1 max-w-md mx-auto">
              Booking confirmations and account alerts will appear here as email/SMS delivery logs — not trip chat.
            </div>
          </div>
        )}
        {visible.map(m => {
          const bookingId = m.meta?.booking_id;
          return (
            <div
              key={m.msg_id}
              className="px-4 sm:px-6 py-4 border-b border-[var(--zc-border)] last:border-0 min-w-0"
              data-testid={`message-${m.msg_id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 min-w-0">
                <div className="flex items-start sm:items-center gap-2 min-w-0 flex-1">
                  <ChannelPill channel={m.channel} />
                  <div className="font-semibold text-sm min-w-0 break-words">{m.subject}</div>
                </div>
                <div className="zc-hint shrink-0 sm:text-right">
                  {new Date(m.created_at).toLocaleString()}
                </div>
              </div>
              <div className="text-sm text-[var(--zc-text-2)] mt-1 break-words">{m.body}</div>
              <div className="zc-hint mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-x-2 gap-y-1 min-w-0 break-words">
                <span>
                  To: {m.to || "—"} · Status:{" "}
                  <span
                    className="font-semibold"
                    style={{
                      color: m.status === "sent" ? "var(--zc-success)"
                        : m.status?.startsWith("skipped") ? "var(--zc-text-3)"
                        : m.status === "sent_mock" ? "var(--zc-warn,#f59e0b)"
                        : "var(--zc-danger,#dc2626)",
                    }}
                  >
                    {m.status}
                  </span>
                </span>
                {bookingId && (
                  <Link
                    to={`/profile/bookings?booking=${bookingId}`}
                    className="inline-flex items-center gap-1 text-[var(--zc-accent)] font-semibold hover:underline"
                    data-testid={`notification-view-trip-${m.msg_id}`}
                  >
                    View trip <ExternalLink size={11} />
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="zc-hint mt-3">
        Booking confirmations are emailed from {SITE_EMAILS.support} when Gmail SMTP is configured. SMS entries remain mocked for now.
      </div>
    </ProfileLayout>
  );
}
